import React from 'react';
import { baseApiUrl, fetcher, ServerErrorResponse, createParamString } from 'data_sources/constants';
import { useInfiniteQuery } from 'react-query';
import useIssuedManuals from 'views/App/Portal/Policy/IssuedManuals/_data_sources/useIssuedManuals';
import type { PaginatedApiCollectionResponse } from 'types/api_response';
import { DTB } from 'views/App/Portal/DTB/_types/dtb';

export async function getDTBs<T extends DTB>(
  orgId: string,
  params: Record<string, any> = {},
  signal?: AbortSignal
): Promise<PaginatedApiCollectionResponse<T>> {
  const url = `${baseApiUrl()}/lexiverse/dtbs/v1/organizations/${orgId}/dtbs`;
  const paramString = createParamString(params);

  try {
    const resp = await fetcher({
      method: 'GET',
      url: `${url}?${paramString}`,
      options: { signal },
    });
    return resp as PaginatedApiCollectionResponse<T>;
  } catch (error: unknown) {
    if (typeof error === 'object' && error !== null && 'errorDetails' in error) {
      throw error as ServerErrorResponse;
    }
    throw error;
  }
}

export default function useDTBs<T extends DTB>(
  orgId: string,
  params: Record<string, any> = {},
  options = {},
  onError?: (errors: ServerErrorResponse) => void
) {
  const dtbQuery = useInfiniteQuery<PaginatedApiCollectionResponse<T>, ServerErrorResponse>(
    ['dtbs', orgId, params],
    async ({ pageParam, signal }): Promise<PaginatedApiCollectionResponse<T>> => {
      try {
        const combinedParams = {
          ...params,
          nextToken: pageParam,
        };

        return await getDTBs<T>(orgId, combinedParams, signal);
      } catch (error: unknown) {
        if (onError && typeof error === 'object' && error !== null && 'errorDetails' in error) {
          onError(error as ServerErrorResponse);
        }
        throw error;
      }
    },
    {
      keepPreviousData: true,
      enabled: !!orgId,
      getNextPageParam: (lastPage) => {
        if (lastPage.nextToken && lastPage.content.length > 0) {
          return lastPage.nextToken;
        }
      },
      ...options,
    }
  );

  const { data: manualsData } = useIssuedManuals(orgId, {}, {});
  // remove merge after name is added on the response
  const mergedData = React.useMemo(() => {
    if (dtbQuery.data && manualsData) {
      const manuals = manualsData.pages.flatMap((page) => page.content);
      const updatedPages = dtbQuery.data.pages.map((page) => ({
        ...page,
        content: page.content.map((dtb) => {
          if (dtb.manual && dtb.manual.id) {
            const found = manuals.find((manual) => manual.id === dtb.manual.id);
            if (found) {
              return {
                ...dtb,
                manual: {
                  ...dtb.manual,
                  manualTitle: found.name,
                },
              };
            }
          }
          return dtb;
        }),
      }));
      return { ...dtbQuery.data, pages: updatedPages };
    }
    return dtbQuery.data;
  }, [dtbQuery.data, manualsData]);

  return {
    ...dtbQuery,
    data: mergedData,
  };
}
